import {
  SpaceBetween,
  KeyValuePairs,
  KeyValuePairsProps,
  Link,
} from '@amzn/awsui-components-react/polaris';
import { SelectProps } from '@amzn/awsui-components-react/polaris/select';
import { Attendance, AttendanceStatus, Event, Option, Question, Shift } from '@amzn/red-velvet-api';
import { useTranslation } from 'react-i18next';
import { useTimezone } from '../../hooks/timezone';
import { asDateString, asTimeString } from '../../utils/dateFormatting';
import { getShiftLengthLabel } from '../eventDisplay';
import { type i18n, TFunction } from 'i18next';
import { formatAttendanceTimeDisplay } from '../../utils/attendance/time';
import { usePaginatedRedVelvetQuery } from '../../hooks/paginatedRedVelvetQuery';
import { getOptionFromMultiValues, getOptionFromValue } from '../../utils/url';
import { MultiselectProps } from '@amzn/awsui-components-react/polaris/multiselect/interfaces';

export interface ModalData {
  loading: boolean;
  waiverLinkOpened: boolean;
  formValidated: boolean;
  questionAnswers?: {
    question: Question;
    options: Option[];
    answer?: Option;
  }[];
}

interface UseShiftInfoProps {
  event: Event;
  shift: Shift;
  attendance?: Partial<Attendance> | undefined;
  displayAttributes: DISPLAY_ATTRIBUTES_TYPE[];
}

export const DISPLAY_LOCATION_INFO = 'location';
export const DISPLAY_DATE_INFO = 'dateInfo';
export const DISPLAY_SIGNUPS_REMAINING_INFO_WITHOUT_CONDITION = 'signupsRemaining';
export const DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION = 'signupsRemaining';
export const DISPLAY_ATTENDANCE_INFO = 'attendance';
export const DISPLAY_COORDINATOR_INFO = 'volunteerCoordinator';
export const DISPLAY_VC_INFO = 'vc';
export type DISPLAY_ATTRIBUTES_TYPE =
  | typeof DISPLAY_DATE_INFO
  | typeof DISPLAY_LOCATION_INFO
  | typeof DISPLAY_ATTENDANCE_INFO
  | typeof DISPLAY_SIGNUPS_REMAINING_INFO_WITHOUT_CONDITION
  | typeof DISPLAY_SIGNUPS_REMAINING_INFO_WITH_CONDITION
  | typeof DISPLAY_COORDINATOR_INFO
  | typeof DISPLAY_VC_INFO;

function generateDateInfo(
  event: Event,
  shift: Shift,
  selectedTimezone: string,
  t: TFunction,
  i18n: i18n
) {
  const startDate = shift.startDateTime ? new Date(shift.startDateTime) : null;
  const endDate = shift.endDateTime ? new Date(shift.endDateTime) : null;
  if (startDate && endDate) {
    const diffTime = endDate.getTime() - startDate.getTime();

    const timeString = asTimeString(startDate, selectedTimezone, i18n.language, {
      removeTimeZone: true,
    });
    const localeStartDateString = asDateString(startDate, selectedTimezone, i18n.language);
    const eventLocalTimeStringNoTz = asTimeString(startDate, event.timeZone, i18n.language, {
      removeTimeZone: true,
    });
    const eventLocalTimeString = asTimeString(startDate, event.timeZone, i18n.language, {
      removeTimeZone: true,
    });
    const localTimeIdentical = eventLocalTimeStringNoTz === timeString;
    const dateInfo = [
      {
        label: t('eventDetails.shiftDisplay.date'),
        // NOTE: this ensures we can add other React elements to the array later,
        // and does not impact the display on the website
        value: <span>{localeStartDateString}</span>,
      },
      {
        label: t('eventDetails.shiftDisplay.length'),
        value: getShiftLengthLabel(t, diffTime),
      },
    ];

    // Volunteer Center created events do not currently support a specific start time, only a start date
    if (event.source !== 'VC') {
      dateInfo.splice(1, 0, {
        label: t('eventDetails.shiftDisplay.startTime2'),
        value: (
          <>
            {timeString}
            {localTimeIdentical ? null : (
              <div className="shiftLocalDateTime">
                <small>
                  <b>{t('eventDetails.shiftDisplay.localStartTime')}:</b> {eventLocalTimeString}
                </small>
              </div>
            )}
          </>
        ),
      });
    }

    return dateInfo;
  } else {
    return [
      {
        label: t('eventDetails.shiftDisplay.date'),
        value: t('eventDisplay.ongoingEvent'),
      },
    ];
  }
}

function generateAttendanceInfo(
  attendanceStatus: string | undefined,
  attendanceMinutes: number | undefined,
  t: TFunction
) {
  const attendanceInfo =
    attendanceStatus !== undefined && attendanceMinutes !== undefined
      ? attendanceStatus === AttendanceStatus.DID_NOT_ATTEND
        ? t('eventDetails.shiftDisplay.didNotAttendantInfo')
        : formatAttendanceTimeDisplay(attendanceMinutes, t)
      : t('eventDetails.shiftDisplay.NoAttendanceTrackInfo');
  return [
    {
      label: t('eventDetails.shiftDisplay.attendance'),
      value: attendanceInfo,
    },
  ];
}

function generateLocationInfo(event: Event, t: TFunction): KeyValuePairsProps.Item[] | null {
  const locationCityWithProvince =
    event.location?.city && event.location?.province
      ? `${event.location.city}, ${event.location.province}`
      : event.location?.city || event.location?.province || null;
  if (locationCityWithProvince) {
    return [
      {
        label: t('eventDetails.location'),
        value: locationCityWithProvince,
      },
    ];
  } else if (event.location?.amazonBuilding) {
    return [
      {
        label: t('eventDetails.location'),
        value: event.location.amazonBuilding,
      },
    ];
  } else if (event.location?.address_string) {
    return [
      {
        label: t('eventDetails.location'),
        value: event.location.address_string,
      },
    ];
  }
  return null;
}

function generateSignupsRemainingInfo(shift: Shift, t: TFunction): KeyValuePairsProps.Item[] {
  const hasLimitedSpots = shift.signupsRemaining && shift.maxSignup;
  const signupsRemaining: KeyValuePairsProps.Item[] = [
    {
      label: t('eventDetails.shiftDisplay.spotsAvailable'),
      value: hasLimitedSpots ? shift.signupsRemaining : t('eventDetails.shiftDisplay.unlimited'),
    },
  ];
  if (hasLimitedSpots) {
    signupsRemaining.unshift({
      label: t('eventDetails.shiftDisplay.signedUp'),
      value: (shift.maxSignup ?? 0) - (shift.signupsRemaining ?? 0),
    });
  }
  return signupsRemaining;
}

function generateEventCoordinatorInfo(
  event: Event,
  personName: string | undefined,
  t: TFunction
): KeyValuePairsProps.Item[] | null {
  const alias =
    event.ownerEmail &&
    event.ownerEmail.includes('@amazon.') &&
    event.ownerEmail.substring(0, event.ownerEmail.indexOf('@'));
  return alias
    ? [
        {
          label: t('eventDetails.shiftDisplay.volunteerCoordinator'),
          value: (
            <>
              {personName || event.ownerEmail}
              <div>
                <Link target="_blank" href={`https://phonetool.amazon.com/users/${alias}`}>
                  {alias}
                </Link>
              </div>
            </>
          ),
        },
      ]
    : null;
}

export function useVcInfo(event: Event): KeyValuePairsProps.Item[] {
  const namespaces = ['attendance', 'translation'];
  const { t } = useTranslation(namespaces);

  const focus = t('focus', { returnObjects: true }) as SelectProps.Options;
  const activity = t('activity', { returnObjects: true }) as SelectProps.Options;
  const sponsors = t('sponsors', { returnObjects: true }) as MultiselectProps.Options;

  const vcInfo: KeyValuePairsProps.Item[] = [
    {
      label: t('editEventInfo.focus'),
      value: getOptionFromValue(event.focusArea || '', focus)?.label,
    },
    {
      label: t('editEventInfo.activity'),
      value: getOptionFromValue(event.primaryActivity || '', activity)?.label,
    },
  ];

  // Ambassadors list is coming through as [''] rather than empty
  if (event.ambassadors && !event.ambassadors.every((item) => item === '')) {
    vcInfo.push({
      label: t('additionalInfo.registeredAmbassadors'),
      value: event.ambassadors
        .map((amb) => amb.split('-')[1]) // grabbing just alias for displaying ['alias-abcde-employeeId-12345678']
        .map<React.ReactNode>((alias) => (
          <Link key={alias} target="_blank" href={`https://phonetool.amazon.com/users/${alias}`}>
            {alias}
          </Link>
        ))
        .reduce((prev, curr) => [prev, ', ', curr]),
    });
  }

  if (event.communityOrg) {
    vcInfo.push({
      label: t('additionalInfo.communityOrganization'),
      value: event.communityOrg,
    });
  }

  if (event.orgPOCName || event.orgPOCEmail) {
    vcInfo.push({
      label: t('additionalInfo.organizationPointOfContact'),
      value: (
        <>
          <span>{event.orgPOCName}</span>
          <div>{event.orgPOCEmail}</div>
        </>
      ),
    });
  }

  if (event.sponsors) {
    vcInfo.push({
      label: t('additionalInfo.elgSponsors'),
      value: getOptionFromMultiValues(event.sponsors, sponsors)
        .map((item) => item.label)
        .join(', '),
    });
  }

  if (event.recap) {
    vcInfo.push({
      label: t('additionalInfo.engagementRecap'),
      value: event.recap.length > 500 ? `${event.recap.slice(0, 500)}...` : event.recap,
    });
  }

  if (event.photosUploadUrl) {
    vcInfo.push({
      label: (
        <Link key={event.photosUploadUrl} external target="_blank" href={event.photosUploadUrl}>
          {t('additionalInfo.photoAlbum')}
        </Link>
      ),
      value: '',
    });
  }

  return vcInfo;
}

export function useShiftInfo({
  event,
  shift,
  attendance,
  displayAttributes,
}: UseShiftInfoProps): KeyValuePairsProps.Item[] {
  const { t, i18n } = useTranslation();
  const selectedTimezone = useTimezone();

  const { queryResult } = usePaginatedRedVelvetQuery({
    redVelvetQueryKey: [
      'getPeople',
      { aliases: event.ownerEmail?.substring(0, event.ownerEmail?.indexOf('@') || 0) },
    ],
  });

  let totalInfo: KeyValuePairsProps.Item[] = [];
  const allAttributes: Record<DISPLAY_ATTRIBUTES_TYPE, KeyValuePairsProps.Item[] | null> = {
    dateInfo: generateDateInfo(event, shift, selectedTimezone, t, i18n),
    location: generateLocationInfo(event, t),
    signupsRemaining: generateSignupsRemainingInfo(shift, t),
    attendance: generateAttendanceInfo(attendance?.attendanceStatus, attendance?.minutes, t),
    volunteerCoordinator: generateEventCoordinatorInfo(
      event,
      queryResult.data && queryResult.data.people && queryResult.data.people.length > 0
        ? `${queryResult.data.people[0].firstName} ${queryResult.data.people[0].lastName}`
        : undefined,
      t
    ),
    vc: useVcInfo(event),
  };
  // Combine all attributes into the totalInfo array based on displayedAttributes
  displayAttributes.forEach((attribute) => {
    const attributeInfo = allAttributes[attribute];
    if (Array.isArray(attributeInfo)) {
      // Check if it's an array
      totalInfo = [...totalInfo, ...attributeInfo];
    }
  });

  return totalInfo;
}

interface ShiftInfoProps {
  event: Event;
  shift: Shift;
  attendance?: Partial<Attendance>;
  displayAttributes: DISPLAY_ATTRIBUTES_TYPE[];
}

export function ShiftInfo({ event, shift, attendance, displayAttributes }: ShiftInfoProps) {
  const { t } = useTranslation();
  const info = useShiftInfo({ event, shift, attendance, displayAttributes });
  return (
    //THIS CLASS IS REQUIRED FOR INTEGRATION TESTING TO REGISTER THE EXISTENCE OF SHIFTS
    <div className="shift">
      <div className="shiftBody">
        <SpaceBetween direction="vertical" size="l">
          <KeyValuePairs
            // since CloudScape allows max 4 cols/row and we currently have 3 -> 6 types of info
            // when we have 3 or 5 info(s), it's better UX to start a new row after the 3rd item vs. 4th item
            // Because shift name is added to any other columns that are provided, we need to add 1 to the
            // length to properly calculate the number of columns to display
            columns={info.length + (1 % 4) === 0 ? 4 : 3}
            items={[
              {
                label: t('eventDetails.shiftDisplay.shiftName'),
                value: shift.name,
              },
              ...info,
            ]}
          />
        </SpaceBetween>
      </div>
    </div>
  );
}
